import clsx from "clsx";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import type { CreateProgramWizardResult } from "../useCreateProgramWizard";

import { Button as ButtonV2 } from "../../../../Button";
import { SuccessIllustration } from "../../../../Icons";
import { Typo } from "../../../../Typo";
import { WizardFooter } from "../WizardFooter";

type SuccessStepProps = {
  title: string;
  result: CreateProgramWizardResult;
  onClose: () => void;
  onViewProgram: () => void;
  isMobile?: boolean;
  footerContainer?: Element | null;
};

export const SuccessStep = ({
  title,
  result,
  onClose,
  onViewProgram,
  isMobile,
  footerContainer,
}: SuccessStepProps) => {
  const { t } = useTranslation();
  const [firstPartner, secondPartner] = result.partners;
  const isTwoSub = result.partners.length > 1;

  const footer = (
    <WizardFooter isMobile={isMobile}>
      <ButtonV2
        variant="tonal"
        size="md"
        onClick={onClose}
        data-testid="create_program_wizard_success_close_button"
      >
        {t("common_close", "Close")}
      </ButtonV2>
      <ButtonV2
        variant="brand"
        size="md"
        onClick={onViewProgram}
        data-testid="create_program_wizard_success_view_program_button"
      >
        {t("subIBReport_viewRebateProgram", "View rebate program")}
      </ButtonV2>
    </WizardFooter>
  );

  return (
    <div
      className={clsx(
        "flex min-h-0 flex-1 flex-col items-start gap-4 text-left",
        isMobile && "pt-3",
      )}
    >
      <SuccessIllustration className="h-16 w-16" aria-hidden="true" />
      <Typo element="h2" type={isMobile ? "headingLG" : "headingXL"}>
        {title}
      </Typo>
      <Typo element="p" type="bodySM" textColor="text-text-secondary">
        {isTwoSub
          ? t(
              "subIBReport_successDescriptionPlural",
              "The referral link is now active. You, {{name1}}, and {{name2}} can immediately start referring clients.",
              {
                name1: firstPartner?.displayName,
                name2: secondPartner?.displayName,
              },
            )
          : t(
              "subIBReport_successDescription",
              "The referral link is now active. You and {{name}} can immediately start referring clients.",
              { name: firstPartner?.displayName },
            )}{" "}
        {/* The canvas appends this to the same paragraph — the split is fixed at
            creation, so a different one means a new program. */}
        {t(
          "subIBReport_successNewProgramHint",
          "If you wish to change the rebate split, create a new program.",
        )}
      </Typo>
      {footerContainer ? createPortal(footer, footerContainer) : footer}
    </div>
  );
};
